import {
  AnyElement,
  FileReaderResponseTypeInBrowser,
  clamp
} from "@zyzgroup/core-common";
import {
  EMPTY,
  Observable,
  combineLatest,
  firstValueFrom,
  from,
  fromEventPattern,
  iif,
  merge,
  of,
  race
} from "rxjs";
import {
  concatMap,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  shareReplay,
  skipWhile,
  startWith,
  switchAll,
  switchMap,
  takeUntil,
  tap,
  throttleTime,
  windowToggle
} from "rxjs/operators";
import {
  createEventListener$,
  HTMLEventObservable,
  stopPropagation,
  preventDefault
} from "./createEventListener";
import { selectFile } from "./dom";
import { atomWriteAttribute } from "./attribute";
import { createGesture } from "./loadHammer";

declare const document: Document & {
  attachEvent: any;
  createEventObject: any;
  onmousewheel: any;
};

export function isSupportTouch(): boolean {
  return (
    "ontouchstart" in window ||
    "ontouchend" in document ||
    navigator.maxTouchPoints > 0
  );
}

export function isSupportPointer(): boolean {
  // return !!(window.PointerEvent || (window as any).MSPointerEvent); // IE10 is prefixed
  return !!window.PointerEvent;
}

/*
 * Detects whether the event "eventName" is supported on a tag with name
 * "nodeName".  Based on code from
 * http://perfectionkills.com/detecting-event-support-without-browser-sniffing/
 */
export function isSupportEvent(nodeName: any, eventName: string): boolean {
  let el = document.createElement(nodeName);
  eventName = "on" + eventName;
  let isSupported = eventName in el;
  if (!isSupported) {
    el.setAttribute(eventName, "return;");
    isSupported = typeof el[eventName] == "function";
  }
  el = null;
  return isSupported;
}

export type MousePoint = {
  x: number;
  y: number;
  isDown: boolean; // 是否已按下
};

export type MousePointGroup = {
  start: MousePoint;
  current: MousePoint;
};

export function createDefaultMousePoint() {
  return {
    x: 0,
    y: 0,
    isDown: false
  };
}

export function createMousePoint() {
  const startMousePoint: MousePoint = createDefaultMousePoint();
  const currentMousePoint: MousePoint = createDefaultMousePoint();

  return {
    start: startMousePoint,
    current: currentMousePoint,

    setStart: function (
      p: { x: number; y: number; isDown?: boolean },
      isDown?: boolean
    ) {
      startMousePoint.x = p.x;
      startMousePoint.y = p.y;
      startMousePoint.isDown =
        typeof isDown == "boolean"
          ? isDown
          : typeof p.isDown == "boolean"
          ? p.isDown
          : false;
      this.setCurrent(p, isDown);
    },

    setCurrent: (
      p: { x: number; y: number; isDown?: boolean },
      isDown?: boolean
    ) => {
      currentMousePoint.x = p.x;
      currentMousePoint.y = p.y;
      currentMousePoint.isDown =
        typeof isDown == "boolean"
          ? isDown
          : typeof p.isDown == "boolean"
          ? p.isDown
          : false;
    }
  };
}

// react browser event names : https://react.dev/reference/react-dom/components/common#common-props

// ** Mouse Events **
export function mouseMove$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return createEventListener$(el, "mousemove", useCapture);
}
export function mouseDown$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return createEventListener$(el, "mousedown", useCapture);
}
export function mouseUp$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return createEventListener$(el, "mouseup", useCapture);
}
export function mouseOver$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return createEventListener$(el, "mouseover", useCapture);
}
export function mouseOut$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return createEventListener$(el, "mouseout", useCapture);
}
export function mouseEnter$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return createEventListener$(el, "mouseenter", useCapture);
}
export function mouseLeave$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return createEventListener$(el, "mouseleave", useCapture);
}
export function mouseClick$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return createEventListener$(el, "click", useCapture);
}
export function mouseDoubleClick$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return createEventListener$(el, "dblclick", useCapture);
}
export function mouseWheel$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return createEventListener$(
    el,
    "onwheel" in document.createElement("div")
      ? "wheel" // modern browsers
      : document.onmousewheel !== undefined
      ? "mousewheel" // Webkit and IE
      : "DOMMouseScroll", // older Firefox
    useCapture
  );
}
export function mouseContextMenu$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<MouseEvent> {
  return createEventListener$(el, "contextmenu", useCapture);
}

// ** Touch Events **
export function touchMove$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<TouchEvent> {
  return createEventListener$(el, "touchmove", useCapture);
}
export function touchStart$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<TouchEvent> {
  return createEventListener$(el, "touchstart", useCapture);
}
export function touchEnd$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<TouchEvent> {
  return createEventListener$(el, "touchend", useCapture);
}
export function touchCancel$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<TouchEvent> {
  return createEventListener$(el, "touchcancel", useCapture);
}

// ** Pointer Events **
export function pointerMove$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<PointerEvent> {
  return createEventListener$(el, "pointermove", useCapture);
}
export function pointerDown$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<PointerEvent> {
  return createEventListener$(el, "pointerdown", useCapture);
}
export function pointerUp$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<PointerEvent> {
  return createEventListener$(el, "pointerup", useCapture);
}
export function pointerCancel$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<PointerEvent> {
  return createEventListener$(el, "pointercancel", useCapture);
}

// ** Hammer Events **  https://hammerjs.github.io/
// Pan : Recognized when the pointer is down and moved in the allowed direction.
export function pan$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("pan", next);
    },
    (next) => {
      gesture.off("pan", next);
    }
  );
  // return new Observable((observer) => {
  //   const pan = (e: HammerInput) => {
  //     observer.next(e);
  //   };
  //   gesture.on("pan", pan);
  //   return () => {
  //     gesture.off("pan", pan);
  //   };
  // });
}
export function panMove$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("panmove", next);
    },
    (next) => {
      gesture.off("panmove", next);
      gesture.destroy();
    }
  );
}
export function panStart$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("panstart", next);
    },
    (next) => {
      gesture.off("panstart", next);
      gesture.destroy();
    }
  );
}
export function panEnd$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("panend", next);
    },
    (next) => {
      gesture.off("panend", next);
      gesture.destroy();
    }
  );
}
export function panCancel$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("pancancel", next);
    },
    (next) => {
      gesture.off("pancancel", next);
      gesture.destroy();
    }
  );
}
export function panLeft$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("panleft", next);
    },
    (next) => {
      gesture.off("panleft", next);
      gesture.destroy();
    }
  );
}
export function panRight$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("panright", next);
    },
    (next) => {
      gesture.off("panright", next);
      gesture.destroy();
    }
  );
}
export function panUp$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("panup", next);
    },
    (next) => {
      gesture.off("panup", next);
      gesture.destroy();
    }
  );
}
export function panDown$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("pandown", next);
    },
    (next) => {
      gesture.off("pandown", next);
      gesture.destroy();
    }
  );
}
// 缩放 Pinch : Recognized when two or more pointers are moving toward (zoom-in) or away from each other (zoom-out).
export function pinch$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("pinch", next);
    },
    (next) => {
      gesture.off("pinch", next);
      gesture.destroy();
    }
  );
}
export function pinchIn$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("pinchin", next);
    },
    (next) => {
      gesture.off("pinchin", next);
      gesture.destroy();
    }
  );
}
export function pinchOut$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("pinchout", next);
    },
    (next) => {
      gesture.off("pinchout", next);
      gesture.destroy();
    }
  );
}
// 长按 Press : Recognized when the pointer is down for x ms without any movement.
export function press$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("press", next);
    },
    (next) => {
      gesture.off("press", next);
      gesture.destroy();
    }
  );
}
// 旋转 Rotate : Recognized when two or more pointer are moving in a circular motion.
export function rotate$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("rotate", next);
    },
    (next) => {
      gesture.off("rotate", next);
      gesture.destroy();
    }
  );
}
// Swipe : Recognized when the pointer is moving fast (velocity), with enough distance in the allowed direction.
export function swipe$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("swipe", next);
    },
    (next) => {
      gesture.off("swipe", next);
      gesture.destroy();
    }
  );
}
export function swipeLeft$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("swipeleft", next);
    },
    (next) => {
      gesture.off("swipeleft", next);
      gesture.destroy();
    }
  );
}
export function swipeRight$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("swiperight", next);
    },
    (next) => {
      gesture.off("swiperight", next);
      gesture.destroy();
    }
  );
}
export function swipeUp$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("swipeup", next);
    },
    (next) => {
      gesture.off("swipeup", next);
      gesture.destroy();
    }
  );
}
export function swipeDown$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("swipedown", next);
    },
    (next) => {
      gesture.off("swipedown", next);
      gesture.destroy();
    }
  );
}
// Tap : Recognized when the pointer is doing a small tap/click.
export function tap$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(el);
  return fromEventPattern(
    (next) => {
      gesture.on("tap", next);
    },
    (next) => {
      gesture.off("tap", next);
      gesture.destroy();
    }
  );
}
// 在 el 之外点击
export function tapOutSide$(
  el: HTMLEventObservable = document.documentElement
): Observable<HammerInput> {
  const gesture = createGesture(document.documentElement);
  return fromEventPattern(
    (next) => {
      const handler = (e: HammerInput) => {
        if (!e.target || el == e.target || el.contains(e.target as Node)) {
          return;
        }
        next(e);
      };
      gesture.on("tap", handler);
      return handler;
    },
    (next, handler) => {
      gesture.off("tap", handler);
      gesture.destroy();
    }
  );
}

// ** Transition Events **
export function transitionStart$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<TransitionEvent> {
  return createEventListener$(el, "transitionstart", useCapture);
}
export function transitionEnd$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<TransitionEvent> {
  return merge(
    createEventListener$(
      el,
      "transitionend",
      useCapture
    ) as Observable<TransitionEvent>,
    createEventListener$(
      el,
      "webkitTransitionEnd",
      useCapture
    ) as Observable<TransitionEvent>,
    createEventListener$(
      el,
      "oTransitionEnd",
      useCapture
    ) as Observable<TransitionEvent>,
    createEventListener$(
      el,
      "MSTransitionEnd",
      useCapture
    ) as Observable<TransitionEvent>
  );
}
export function transitionCancel$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<TransitionEvent> {
  return createEventListener$(el, "transitioncancel", useCapture);
}

// ** Animation Events **
export function animationStart$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<AnimationEvent> {
  return createEventListener$(el, "animationstart", useCapture);
}
export function animationEnd$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<AnimationEvent> {
  return createEventListener$(el, "animationend", useCapture);
}
export function animationCancel$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<AnimationEvent> {
  return createEventListener$(el, "animationcancel", useCapture);
}

// ** Drag Drop Events **
export function drag$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<DragEvent> {
  return createEventListener$(el, "drag", useCapture);
}
export function dragStart$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<DragEvent> {
  return createEventListener$(el, "dragstart", useCapture);
}
export function dragEnd$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<DragEvent> {
  return createEventListener$(el, "dragend", useCapture);
}
export function drop$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<DragEvent> {
  return createEventListener$(el, "drop", useCapture);
}
export function dragEnter$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<DragEvent> {
  return createEventListener$(el, "dragenter", useCapture);
}
export function dragLeave$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<DragEvent> {
  return createEventListener$(el, "dragleave", useCapture);
}
export function dragOver$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<DragEvent> {
  return createEventListener$(el, "dragover", useCapture);
}

// ** Keyboard Events **
export function keyDown$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<KeyboardEvent> {
  return createEventListener$(el, "keydown", useCapture);
}
export function keyUp$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<KeyboardEvent> {
  return createEventListener$(el, "keyup", useCapture);
}
export function keyPress$(
  el: HTMLEventObservable = document.documentElement,
  useCapture = false
): Observable<KeyboardEvent> {
  return createEventListener$(el, "keypress", useCapture);
}

// ** html Events **
// elChange$(el).subscribe((e) =>
//   setPerson({
//     ...person,
//     [e.target.name]: e.target.value
//     [e.target.name]: e.target.checked // radio checkbox
//     [e.target.name]: e.target.selected // select
//   })
// );
// const group = document.querySelector(".group"); // <p class='group'>...</p>
// elChange$(group).subscribe((e) => {
//   const checked = group.querySelector(":checked");
//   console.log(checked.value);
//   const selected = group.querySelector(":selected");
//   console.log(selected.value);
// });
export function elChange$(
  el: HTMLSelectElement | HTMLInputElement,
  useCapture = false
): Observable<MouseEvent> {
  return createEventListener$(el, "change", useCapture);
}

export function formSubmit$(
  el: HTMLFormElement,
  useCapture = false
): Observable<MouseEvent> {
  return createEventListener$(el, "submit", useCapture);
}

// ** global Events **

// All events propagate in React except onScroll, which only works on the JSX tag you attach it to.
export function scroll$(time = 10): Observable<Event> {
  // window.scrollX;
  // window.scrollY;
  return createEventListener$(window, "scroll", true).pipe(throttleTime(time));
}

export function documentVisible$(): Observable<boolean> {
  return createEventListener$(document, "visibilitychange", true).pipe(
    map(() => {
      // return !document.hidden;
      return document.visibilityState === "visible";
    }),
    distinctUntilChanged()
  );
  // return combineLatest([
  //   createEventListener$(window, "focus", true),
  //   createEventListener$(window, "pagehide", true),
  //   createEventListener$(window, "blur", true)
  // ]).pipe(
  //   map(([_, __, ___]) => {
  //     return !document.hidden;
  //   }),
  //   distinctUntilChanged()
  // );
}

// 监听 localStorage , storageEvent.key == localStorage.key
export function storageEvent$(): Observable<StorageEvent> {
  return createEventListener$<StorageEvent>(window, "storage", false);
}

export function createOnlineStatus$(): Observable<boolean> {
  return merge(
    createEventListener$<Event>(window, "online", false),
    createEventListener$<Event>(window, "offline", false)
  ).pipe(
    map(() => navigator.onLine),
    startWith(navigator.onLine),
    distinctUntilChanged(),
    shareReplay({
      bufferSize: 1, // 每个新订阅会收到最近一次事件
      refCount: true // 最后一个订阅取消时会自动解绑事件
    })
  );
}

export function beforeUnload$(): Observable<BeforeUnloadEvent> {
  return createEventListener$<BeforeUnloadEvent>(window, "beforeunload", false);
}

////////////////////// Mouse 或 Touch 或 Pointer 或 gesture //////////////////////

type MousePointMoveOptions = {
  start?: (e: MousePointEvent, mpg: MousePointGroup) => void;
  end?: (e: MousePointEvent, mpg: MousePointGroup) => void;
  createMousePoint?: ReturnType<typeof createMousePoint>;
};

export function mouseMovePoint$(
  el: HTMLElement = document.documentElement,
  options?: Pick<MousePointMoveOptions, "createMousePoint">
): Observable<MousePointGroup> {
  const mpg = options?.createMousePoint || createMousePoint();
  return mouseMove$(el).pipe(
    map((e) => {
      mpg.setCurrent(getMousePositionInElement(e, el));
      return mpg;
    })
  );
}

export function mouseDownMovePoint$(
  el: HTMLElement = document.documentElement,
  options?: MousePointMoveOptions
): Observable<MousePointGroup> {
  const mpg = options?.createMousePoint || createMousePoint();
  return mouseMove$(el).pipe(
    windowToggle(
      mouseDown$(el).pipe(
        tap((e) => {
          mpg.setStart(getMousePositionInElement(e, el), true);
          options?.start?.(e, mpg);
        })
      ),
      () =>
        mouseUp$(el).pipe(
          tap((e) => {
            mpg.setCurrent(getMousePositionInElement(e, el), false);
            options?.end?.(e, mpg);
          })
        )
    ),
    switchAll(),
    map((e) => {
      mpg.setCurrent(getMousePositionInElement(e, el), true);
      return mpg;
    })
  );
}

export function touchDownMovePoint$(
  el: HTMLElement = document.documentElement,
  options?: MousePointMoveOptions
): Observable<MousePointGroup> {
  const mpg = options?.createMousePoint || createMousePoint();
  return touchStart$(el).pipe(
    tap((e) => {
      const touch = e.changedTouches[0]; // first touch
      mpg.setStart(getMousePositionInElement(touch, el), true);
      options?.start?.(touch, mpg);
    }),
    switchMap(() =>
      touchMove$(el).pipe(
        takeUntil(
          race(touchEnd$(el), touchCancel$(el)).pipe(
            tap((e) => {
              const touch = e.changedTouches[0]; // first touch
              mpg.setCurrent(getMousePositionInElement(touch, el), false);
              options?.end?.(touch, mpg);
            })
          )
        )
      )
    ),
    map((e) => {
      const touch = e.changedTouches[0]; // first touch
      mpg.setCurrent(getMousePositionInElement(touch, el), true);
      return mpg;
    })
  );
}

// 在 mobile 下似乎 pointerUp$ pointerCancel$ 会提前触发？
export function pointerDownMovePoint$(
  el: HTMLElement = document.documentElement,
  options?: MousePointMoveOptions
): Observable<MousePointGroup> {
  const mpg = options?.createMousePoint || createMousePoint();
  return pointerDown$(el).pipe(
    tap((e) => {
      mpg.setStart(getMousePositionInElement(e, el), true);
      options?.start?.(e, mpg);
    }),
    switchMap(() =>
      pointerMove$(el).pipe(
        takeUntil(
          race(pointerUp$(el), pointerCancel$(el)).pipe(
            tap((e) => {
              mpg.setCurrent(getMousePositionInElement(e, el), false);
              options?.end?.(e, mpg);
            })
          )
        )
      )
    ),
    map((e) => {
      mpg.setCurrent(getMousePositionInElement(e, el), true);
      return mpg;
    })
  );
}

// 大一统
export function compatableDownMovePoint$(
  el: HTMLElement = document.documentElement,
  options?: MousePointMoveOptions
): Observable<MousePointGroup> {
  return race(
    mouseDownMovePoint$(el, options),
    touchDownMovePoint$(el, options)
    // pointerDownMovePoint$(el, options),
  );
}

export function mouseAndPointerDownMovePoint$(
  el: HTMLElement = document.documentElement,
  options?: MousePointMoveOptions
): Observable<MousePointGroup> {
  const mpg = options?.createMousePoint || createMousePoint();
  return merge(mouseMove$(el), pointerDown$(el)).pipe(
    exhaustMap((e) => {
      if (e instanceof PointerEvent) {
        // pointerDown
        mpg.setStart(getMousePositionInElement(e, el), true);
        options?.start?.(e, mpg);
        return pointerMove$(el).pipe(
          takeUntil(
            race(pointerUp$(el), pointerCancel$(el)).pipe(
              tap((e) => {
                mpg.setCurrent(getMousePositionInElement(e, el), false);
                options?.end?.(e, mpg);
              })
            )
          )
        );
      } else if (e instanceof MouseEvent) {
        // mouseMove
        return of(e);
      } else {
        return EMPTY;
      }
    }),
    map((e) => {
      mpg.setCurrent(
        getMousePositionInElement(e, el),
        e instanceof PointerEvent
      );
      return mpg;
    })
  );
}

/**
 * 下拉刷新 pull-downing dStartY > 0
 * 上拉加载 pull-uping dStartY < 0
 */
export function pull$(el: HTMLElement): Observable<number> {
  return new Observable<number>((observer) => {
    const subscription = pointerDownMovePoint$(el, {
      start: () => {
        observer.next(0);
      }
    }).subscribe((mpg) => {
      observer.next(mpg.current.y - mpg.start.y);
    });
    return subscription.unsubscribe;
  }).pipe(distinctUntilChanged());
}

// <div draggable="true" ondragstart="dragstart" ondragend="dragend">Draggable-Item</div>
export function createDraggable(
  el: HTMLElement,
  options?: {
    onStart?: (e: DragEvent) => void;
    onDrag?: (e: DragEvent) => void;
    onEnd?: (e: DragEvent) => void;
  }
): () => void {
  atomWriteAttribute(el, "draggable", "true");

  const dragStartSubscription = dragStart$(el, true).subscribe(
    (e: DragEvent) => {
      preventDefault(e);
      stopPropagation(e);
      const dt = e.dataTransfer;
      if (dt) {
        dt.effectAllowed = "move";
        // you must set some data in order to drag an arbitrary block element like a <div>
        dt.setData("text/plain", "some-data");
        // const coords = { x: e.offsetX, y: e.offsetY };
        // if (dt.setDragImage) {
        //   dt.setDragImage(el, coords.x, coords.y);
        // }
      }
      options?.onStart?.(e);
    }
  );

  const dragSubscription = drag$(el).subscribe((e: DragEvent) => {
    preventDefault(e);
    options?.onDrag?.(e);
  });

  const dragEndSubscription = dragEnd$(el).subscribe((e: DragEvent) => {
    preventDefault(e);
    options?.onEnd?.(e);
  });

  return () => {
    dragStartSubscription.unsubscribe();
    dragSubscription.unsubscribe();
    dragEndSubscription.unsubscribe();
  };
}

// <div ondragenter="dragenter" ondragleave="dragleave" ondragover="dragover" ondrop="drop">Drop-Zone</div>
export function createDrop(
  el: HTMLElement,
  options?: {
    onEnter?: (e: DragEvent) => void;
    onLeave?: (e: DragEvent) => void;
    onOver?: (e: DragEvent) => void;
    onDrop?: (e: DragEvent, files: File[]) => void;
  }
): () => void {
  const dragEnterSubscription = dragEnter$(el).subscribe((e: DragEvent) => {
    preventDefault(e);
    options?.onEnter?.(e);
  });

  const dragOverSubscription = dragOver$(el).subscribe((e: DragEvent) => {
    preventDefault(e);
    const dt = e.dataTransfer;
    if (dt) {
      dt.dropEffect = "move";
    }
    options?.onOver?.(e);
  });

  const dragLeaveSubscription = dragLeave$(el).subscribe((e: DragEvent) => {
    preventDefault(e);
    options?.onLeave?.(e);
  });

  const dropSubscription = drop$(el).subscribe((e: DragEvent) => {
    preventDefault(e);
    const dt = e.dataTransfer;
    if (!dt) {
      options?.onDrop?.(e, []);
      return;
    }
    options?.onDrop?.(e, [...dt.files]);
    // const data = dt.getData("text");
    // dt.clearData();
    // for (const item of dt.items) {
    //   if (item.kind === "string") {
    //     if (item.type.match("^text/plain")) {
    //       item.getAsString((s) => {
    //         // string
    //       });
    //     } else if (item.type.match("^text/html")) {
    //       // HTML
    //     } else if (item.type.match("^text/uri-list")) {
    //       // URI
    //     }
    //   } else if (item.kind === "file") {
    //     if (item.type.match("^image/")) {
    //       // image file
    //     }
    //     const f = item.getAsFile();
    //   }
    // }
  });

  return () => {
    dragEnterSubscription.unsubscribe();
    dragOverSubscription.unsubscribe();
    dragLeaveSubscription.unsubscribe();
    dropSubscription.unsubscribe();
  };
}

////////////////////// Position //////////////////////

export function getWindow() {
  return {
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    innerWidth: window.innerWidth, // 浏览器窗口的宽度（不包括滚动条）
    innerHeight: window.innerHeight, // 浏览器窗口的高度（不包括滚动条）
    screenX: window.screenX,
    screenY: window.screenY, // window.screenY + (window.outerHeight - window.innerHeight)
    pageXOffset: window.pageXOffset, // 当前页面水平滚动的像素数
    pageYOffset: window.pageYOffset // 当前页面垂直滚动的像素数
  };
}

// For any non-empty HTML document, documentElement will always be an <html> element.
// For any non-empty XML document, documentElement will always be whatever element is the root element of the document.
// The Document.body property represents the <body> or <frameset> node of the current document, or null if no such element exists.
export function getDocumentElement() {
  const el = document.documentElement || document.body;
  // // IE浏览器
  // if (document.compatMode === "CSS1Compat") {
  //   // 标准模式
  // } else {
  //   // 怪异模式
  // }
  return {
    el,

    // 不包括边框大小，只包含内容和内边距
    clientWidth: el.clientWidth,
    clientHeight: el.clientHeight,
    clientTop: el.clientTop,
    clientLeft: el.clientLeft,

    // const contentWidth =
    //   [...el.children].reduce(
    //     (a, e) => Math.max(a, e.getBoundingClientRect().right),
    //     0
    //   ) - el.getBoundingClientRect().x;

    // 元素的内容区域加上它的内边距再加上任何溢出内容的尺寸
    // 当内容没有溢出时，scrollWidth == clientWidth , scrollHeight == clientHeight
    // 当内容溢出时，   scrollWidth >  clientWidth , scrollHeight >  clientHeight
    scrollWidth: el.scrollWidth,
    scrollHeight: el.scrollHeight,
    scrollTop: el.scrollTop,
    scrollLeft: el.scrollLeft,

    // 返回元素的实际大小，包括边框、内边距和滚动条
    offsetWidth: el.offsetWidth,
    offsetHeight: el.offsetHeight,
    offsetTop: el.offsetTop,
    offsetLeft: el.offsetLeft,

    offsetParent: el.offsetParent
  };
}

// 获取可视区域
export function getViewport() {
  const { clientWidth, clientHeight, scrollLeft, scrollTop } =
    getDocumentElement();
  return {
    width: window.innerWidth || clientWidth,
    height: window.innerHeight || clientHeight,
    x: window.pageXOffset || scrollLeft,
    y: window.pageYOffset || scrollTop
  };
}

// 获取页面整体
export function getPage() {
  const { scrollWidth, scrollHeight, scrollLeft, scrollTop } =
    getDocumentElement();
  return {
    width: scrollWidth,
    height: scrollHeight,
    scrollX: window.pageXOffset || scrollLeft,
    scrollY: window.pageYOffset || scrollTop
  };
}

export function getElementPositionInViewport(el: HTMLElement) {
  return el.getBoundingClientRect();
}

export function getRelativeBoundingClientRect(child: HTMLElement) {
  return {
    x: child.offsetLeft,
    y: child.offsetTop,
    width: child.offsetWidth,
    height: child.offsetHeight
  };
}

export function getElementPositionInPage(
  el: HTMLElement | SVGElement | Element
) {
  const { top, left, width, height } = el.getBoundingClientRect();
  const { scrollLeft, scrollTop } = getDocumentElement();
  return {
    x: left + scrollLeft,
    y: top + scrollTop,
    width,
    height
  };
}

export function getElementPositionInPage2(el: HTMLElement) {
  let elem: HTMLElement | null = el;
  let actualLeft = 0;
  let actualTop = 0;
  while (elem !== null) {
    actualLeft += elem.offsetLeft;
    actualTop += elem.offsetTop;
    if (elem.offsetParent instanceof HTMLElement) {
      elem = elem.offsetParent;
    } else {
      elem = null;
    }
  }
  return {
    x: actualLeft,
    y: actualTop,
    width: el.offsetWidth,
    height: el.offsetHeight
  };
}

export function getVerticalScrollTopPercentInPage() {
  const { clientHeight, scrollTop, scrollHeight } = getDocumentElement();
  const height = scrollHeight - clientHeight;
  const percent = height <= 0 ? 0 : (scrollTop / height) * 100;
  return percent;
}

// 判断元素是否在视口内
export function isInViewport(el: HTMLElement): boolean {
  const viewport = getViewport();
  const rect = getElementPositionInViewport(el);
  return (
    rect.bottom >= 0 &&
    rect.top <= viewport.height &&
    rect.right >= 0 &&
    rect.left <= viewport.width
  );
}

// export function getComputedPosition(el: HTMLElement) {
//   const styles = atomReadStyles(el);
//   const transform = readStyleFromStyles(styles, "transform");
//   let x = 0;
//   let y = 0;
//   if (transform && transform !== "none") {
//     // 匹配 matrix() matrix3d()
//     const matrixValues = transform.match(/matrix[^()]*\(([^()]+)\)/);
//     if (matrixValues && matrixValues[1]) {
//       const matrix = matrixValues[1].split(", ").map(Number);
//       x = +(matrix[12] || matrix[4]) || 0;
//       y = +(matrix[13] || matrix[5]) || 0;
//     }

//     // 匹配 translateX() 和 translateY()
//     const translateX = transform.match(/translateX\((-?[\d.]+)px\)/);
//     const translateY = transform.match(/translateY\((-?[\d.]+)px\)/);
//     if (translateX) {
//       x = parseFloat(translateX[1]);
//     }
//     if (translateY) {
//       y = parseFloat(translateY[1]);
//     }

//     // 匹配 translate()
//     const translate = transform.match(
//       /translate\((-?[\d.]+)px, (-?[\d.]+)px\)/
//     );
//     if (translate) {
//       x = parseFloat(translate[1]);
//       y = parseFloat(translate[2]);
//     }

//     // 匹配 scaleX() 和 scaleY()
//     // const scaleX = transform.match(/scaleX\((-?[\d.]+)\)/);
//     // const scaleY = transform.match(/scaleY\((-?[\d.]+)\)/);

//     // 匹配 rotate()
//     // const rotate = transform.match(/rotate\((-?[\d.]+)deg\)/);
//   } else {
//     x = +styles.left.replace(/[^-\d.]/g, "") || 0;
//     y = +styles.top.replace(/[^-\d.]/g, "") || 0;
//   }
//   return { x: x, y: y };
// }

////////////////////// Position : Mouse 或 Touch 或 Pointer 或 gesture //////////////////////

export type MousePointEvent = MouseEvent | Touch | PointerEvent | HammerInput;

export function getMousePositionInViewport(ev: MousePointEvent) {
  if ("center" in ev) {
    const center = (ev as HammerInput).center;
    const rect = getElementPositionInViewport(ev.target);
    return {
      x: center.x - rect.x,
      y: center.y - rect.y
    };
  }
  return {
    x: ev.clientX,
    y: ev.clientY
  };
}

export function getMousePositionInPage(ev: MousePointEvent) {
  if ("center" in ev) {
    return (ev as HammerInput).center;
  }
  if (typeof ev.pageX == "number" && typeof ev.pageY == "number") {
    return {
      x: ev.pageX,
      y: ev.pageY
    };
  }
  const { scrollLeft, scrollTop } = getDocumentElement();
  return {
    x: ev.clientX + scrollLeft,
    y: ev.clientY + scrollTop
  };
}

export function getMousePositionInElement(
  ev: MousePointEvent,
  element?: AnyElement
) {
  const r = {
    x: 0,
    y: 0
  };
  if (!element) {
    /// 原 offset 只是相对于直接父类
    let fixedOffsetX = 0;
    let fixedOffsetY = 0;
    if (
      "currentTarget" in ev &&
      ev.currentTarget &&
      ev.target &&
      ev.currentTarget != ev.target
      // ev.target: Refers to the actual element where the event occurred
      // ev.currentTarget: Refers to the element to which the event handler is attached
    ) {
      const currentTargetRect = (
        ev.currentTarget as AnyElement
      ).getBoundingClientRect();
      const targetRect = (ev.target as AnyElement).getBoundingClientRect();
      fixedOffsetX = targetRect.left - currentTargetRect.left;
      fixedOffsetY = targetRect.top - currentTargetRect.top;
    }
    if ("offsetX" in ev) {
      r.x = ev.offsetX + fixedOffsetX;
      r.y = ev.offsetY + fixedOffsetY;
    } else {
      const mouse = getMousePositionInPage(ev);
      const p = getElementPositionInPage(ev.target as AnyElement);
      r.x = clamp(mouse.x - p.x + fixedOffsetX, 0, p.width);
      r.y = clamp(mouse.y - p.y + fixedOffsetY, 0, p.height);
    }
  } else {
    const mouse = getMousePositionInPage(ev);
    const p = getElementPositionInPage(element);
    r.x = clamp(mouse.x - p.x, 0, p.width);
    r.y = clamp(mouse.y - p.y, 0, p.height);
  }
  return r;
}

/**
 * Given a mouse scroll wheel event, get the "delta" of how fast it moved.
 * @param {Object} e - a DOM Event Object.
 * @return {int} - the mouse wheel's delta.  It is greater than 0, the
 * scroll wheel was spun upwards; if less than 0, downwards.
 */
export function getScrollWheelDelta(e: any) {
  let delta = 0;
  if (!e) /* For IE. */ e = window.event;
  if (e.wheelDelta) {
    /* IE/Opera. */
    delta = e.wheelDelta / 120;
    /** In Opera 9, delta differs in sign as compared to IE.
     */
    if ((window as any).opera) {
      delta = -delta;
    }
  } else if (e.detail) {
    /** Mozilla case. */
    /** In Mozilla, sign of delta is different than in IE.
     * Also, delta is multiple of 3.
     */
    delta = -e.detail / 3;
  }
  return delta;
}

// 0 — Left Button (the default)
// 1 — Middle Button (currently unsupported)
// 2 — Right Button
// 3 — X1 (Back) Button
// 4 — X2 (Forward) Button
export function getMouseButton(event: MouseEvent) {
  if (document.implementation.hasFeature("MouseEvents", "2.0")) {
    return event.button;
  } else {
    switch (event.button) {
      case 0:
      case 1:
      case 3:
      case 5:
      case 7:
        return 0; // 左键
      case 2:
      case 6:
        return 2; // 右键
      case 4:
        return 1; // 中键
    }
  }
}

////////////////////// Keyboard //////////////////////

export const CommonKeyCode = {
  BACKSPACE: 8,
  TAB: 9,
  COMMA: 188,
  DELETE: 46,
  END: 35,
  ENTER: 13,
  ESCAPE: 27,
  HOME: 36,
  LEFT: 37,
  UP: 38,
  DOWN: 40,
  PAGE_DOWN: 34,
  PAGE_UP: 33,
  PERIOD: 190,
  RIGHT: 39,
  SPACE: 32
};

/**
 * Representations of pressable keys that aren't text.  These are stored in
 * the Unicode PUA (Private Use Area) code points, 0xE000-0xF8FF.  Refer to
 * http://www.google.com.au/search?&q=unicode+pua&btnK=Search
 *
 * @enum {string}
 * @see <https://www.w3.org/TR/webdriver/#keyboard-actions>
 */
const Key = {
  NULL: "\uE000",
  CANCEL: "\uE001", // ^break
  HELP: "\uE002",
  BACK_SPACE: "\uE003",
  TAB: "\uE004",
  CLEAR: "\uE005",
  RETURN: "\uE006",
  ENTER: "\uE007",
  SHIFT: "\uE008",
  CONTROL: "\uE009",
  ALT: "\uE00A",
  PAUSE: "\uE00B",
  ESCAPE: "\uE00C",
  SPACE: "\uE00D",
  PAGE_UP: "\uE00E",
  PAGE_DOWN: "\uE00F",
  END: "\uE010",
  HOME: "\uE011",
  ARROW_LEFT: "\uE012",
  LEFT: "\uE012",
  ARROW_UP: "\uE013",
  UP: "\uE013",
  ARROW_RIGHT: "\uE014",
  RIGHT: "\uE014",
  ARROW_DOWN: "\uE015",
  DOWN: "\uE015",
  INSERT: "\uE016",
  DELETE: "\uE017",
  SEMICOLON: "\uE018",
  EQUALS: "\uE019",

  NUMPAD0: "\uE01A", // number pad keys
  NUMPAD1: "\uE01B",
  NUMPAD2: "\uE01C",
  NUMPAD3: "\uE01D",
  NUMPAD4: "\uE01E",
  NUMPAD5: "\uE01F",
  NUMPAD6: "\uE020",
  NUMPAD7: "\uE021",
  NUMPAD8: "\uE022",
  NUMPAD9: "\uE023",
  MULTIPLY: "\uE024",
  ADD: "\uE025",
  SEPARATOR: "\uE026",
  SUBTRACT: "\uE027",
  DECIMAL: "\uE028",
  DIVIDE: "\uE029",

  F1: "\uE031", // function keys
  F2: "\uE032",
  F3: "\uE033",
  F4: "\uE034",
  F5: "\uE035",
  F6: "\uE036",
  F7: "\uE037",
  F8: "\uE038",
  F9: "\uE039",
  F10: "\uE03A",
  F11: "\uE03B",
  F12: "\uE03C",

  COMMAND: "\uE03D", // Apple command key
  META: "\uE03D", // alias for Windows key

  /**
   * Japanese modifier key for switching between full- and half-width
   * characters.
   * @see <https://en.wikipedia.org/wiki/Language_input_keys>
   */
  ZENKAKU_HANKAKU: "\uE040"
};

export function getKeyCode(e: KeyboardEvent) {
  if (e.key) {
    return e.key; // e.key='u' | e.code='KeyU'
  } else if (e.keyCode) {
    return e.keyCode;
  } else if (e.which) {
    return e.which;
  }
}

// 注意：不要与浏览器系统快捷键重合！
export interface HotKeyOptions {
  keys: string[];
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  target?: EventTarget;
}

/// UNDO 判断多个 keys 同时按下
function matchHotKeyScheme(
  event: KeyboardEvent, // WheelEvent | KeyboardEvent
  options: HotKeyOptions
): boolean {
  const ctrlKey = options.ctrl || false;
  const shiftKey = options.shift || false;
  const altKey = options.alt || false;
  return (
    (ctrlKey === event.metaKey || ctrlKey === event.ctrlKey) &&
    shiftKey === event.shiftKey &&
    altKey === event.altKey &&
    options.keys.map((k) => k.toLowerCase()).includes(event.key.toLowerCase())
  );
}

// UNDO pause
// UNDO resume

// 注意：不要与浏览器系统快捷键重合！
export function hotKeyDown$(options: HotKeyOptions): Observable<void> {
  return keyDown$(options.target || document || window).pipe(
    filter((e: KeyboardEvent) => matchHotKeyScheme(e, options)),
    map((e: KeyboardEvent) => {
      e.stopPropagation();
      e.preventDefault();
      return;
    })
  );
}

// 注意：不要与浏览器系统快捷键重合！
export function hotKeyUp$(options: HotKeyOptions): Observable<void> {
  return keyUp$(options.target || document || window).pipe(
    filter((e: KeyboardEvent) => matchHotKeyScheme(e, options)),
    map((e: KeyboardEvent) => {
      e.stopPropagation();
      e.preventDefault();
      return;
    })
  );
}

// 注意：不要与浏览器系统快捷键重合！
export function hotKeyFile$(
  hotKeyOptions: HotKeyOptions = { keys: ["o"] },
  type: FileReaderResponseTypeInBrowser = FileReaderResponseTypeInBrowser.text
) {
  return hotKeyDown$(hotKeyOptions).pipe(
    // 方法1：使用 switchMap 调用异步函数
    switchMap(async () => {
      return await selectFile(type);
    })
    // 方法2：将 Promise 转为 Observable
    // switchMap(() => {
    //   return from(selectFile(type));
    // })
  );
}

// 注意：不要与浏览器系统快捷键重合！
export async function hotKeyFile(
  hotKeyOptions: HotKeyOptions = { keys: ["o"] },
  type: FileReaderResponseTypeInBrowser = FileReaderResponseTypeInBrowser.text
) {
  return firstValueFrom(hotKeyFile$(hotKeyOptions, type)); // 将 Observable 转为 Promise
}
